import { PaymentMethod, useBillingContext } from '@shipstation/financial-services-ui-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import { useFsuiContext } from '../../../../../../financial-services-ui/fsui-context-provider';
import { mapBillingInfoDataToBillingAddress } from '../../../../../../financial-services-ui/mapping';
import { useHumanScript } from '../../../../../../hooks/use-human-script';
import { ADD_PAYMENT_METHOD_DATA, useDeprecatedLocalStorage } from '../../../../../../hooks/use-local-storage';
import { useLocale } from '../../../../../../intl/hooks';
import Global from '../../../../../../store/global-selectors';
import { getSellerLight } from '../../../../../../store/seller/seller.actions';
import { updateCompanyNameAndPhone, updateSellerBillingInfo } from '../../../../../setup/api/setup.api';
import { TaxFormContextProvider, useTaxFormContext } from '../../../context/tax-form-context-provider';
import { PageEventSourceStrings } from '../../../hooks/use-track-event';
import { ManageModal } from '../manage-modal';
import { AddPaymentMethodBody } from './add-payment-method-body';
import { messages } from './messages';
import styles from './styles.module.less';

/**
 * @param {object} props
 * @param {() => void} props.onCancel
 * @param {() => void} props.onConfirm
 * @param {unknown} [props.additionalStorePayload]
 * @param {string} [props.dataSource]
 * @param {boolean} [props.isPolling]
 * @returns {JSX.Element}
 */
const AddPaymentMethodModalContent = ({
  onCancel,
  onConfirm,
  dataSource = ADD_PAYMENT_METHOD_DATA,
  additionalStorePayload = emptyObject,
  isPolling = false,
}) => {
  const { preloadedSession, isPreloadingSession, onPaymentCompleted, onError } = useFsuiContext();
  const dispatch = useDispatch();
  const { setBillingInfo, billingInfo } = useBillingContext();
  const { submitTaxForm, isTaxSectionOpen, isEditing: isEditingTaxForm } = useTaxFormContext();

  // Store latest values in ref so handleSuccess always reads fresh values
  // (even though FSUI captured handleSuccess early with initial values)
  const taxFormStateRef = useRef({ isTaxSectionOpen, isEditingTaxForm, submitTaxForm });
  taxFormStateRef.current = { isTaxSectionOpen, isEditingTaxForm, submitTaxForm };

  const [selectedType, setSelectedType] = useState(null);
  const submitFunctionRef = useRef(noop);
  const [submitFunction, setSubmitFunction] = useState(null);
  const [, setDataToStorage] = useDeprecatedLocalStorage(dataSource, emptyObject);
  const { seller } = useSelector((state) => state);
  const { phone, companyName } = seller || emptyObject;

  const { locale } = useLocale();
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);

  useHumanScript();

  const getShouldUpdateCompanyOrPhone = useCallback(
    (suppliedBillingInfo) => {
      return companyName !== (suppliedBillingInfo?.company || '') || phone !== (suppliedBillingInfo?.phone || '');
    },
    [companyName, phone],
  );

  const handleError = useCallback(() => {
    if (!preloadedSession) {
      return;
    }
    onError();
  }, [onError, preloadedSession]);

  const handleOnCancel = useCallback(() => {
    setBillingInfo(null);
    onCancel();
  }, [onCancel, setBillingInfo]);

  const handleSuccess = useCallback(() => {
    const { isTaxSectionOpen: isSectionOpen, isEditingTaxForm: isEditing, submitTaxForm: submitForm } = taxFormStateRef.current;

    if (isEditing && isSectionOpen) {
      submitForm();
    }

    setBillingInfo(null);
    onConfirm();
  }, [onConfirm, setBillingInfo]);

  const handlePaymentCompleted = useCallback(
    (result) => {
      onPaymentCompleted(result, handleSuccess, handleError);
    },
    [onPaymentCompleted, handleSuccess, handleError],
  );

  const handleBeforeRedirect = useCallback(async () => {
    if (!preloadedSession) {
      return null;
    }

    const shouldUpdateCompanyOrPhone = getShouldUpdateCompanyOrPhone(billingInfo);
    if (shouldUpdateCompanyOrPhone) {
      try {
        await updateCompanyNameAndPhone(billingInfo?.company, billingInfo?.phone);
        dispatch(getSellerLight());
      } catch {
        // eat error
      }
    }

    setDataToStorage({
      billingInfo,
      preloadedSession,
      payload: additionalStorePayload,
    });
  }, [billingInfo, dispatch, getShouldUpdateCompanyOrPhone, preloadedSession, setDataToStorage, additionalStorePayload]);

  const setFsuiSubmitPaymentFunction = useCallback((submit) => {
    setSubmitFunction(() => submit);
  }, []);

  const creditCardConfig = useMemo(
    () => ({
      currencyCode: sellerDefaultCurrencyCode,
      inputBorderRadius: '4px',
      inputHeight: '32px',
      locale,
      onError: handleError,
      onPaymentCompleted: handlePaymentCompleted,
      returnUrl: window.location.origin,
      submitPayment: setFsuiSubmitPaymentFunction,
      transactionCategory: null,
    }),
    [handleError, handlePaymentCompleted, setFsuiSubmitPaymentFunction, locale, sellerDefaultCurrencyCode],
  );

  const payByBankUsConfig = useMemo(
    () => ({
      beforeRedirect: handleBeforeRedirect,
      currencyCode: sellerDefaultCurrencyCode,
      locale,
      session: preloadedSession,
      onError: handleError,
      onPaymentCompleted: noop, // unused in PBB directly because of redirect
      returnUrl: window.location.href,
      submitPayment: setFsuiSubmitPaymentFunction,
      transactionCategory: null,
    }),
    [handleBeforeRedirect, handleError, locale, preloadedSession, setFsuiSubmitPaymentFunction, sellerDefaultCurrencyCode],
  );

  const handleManualSubmit = useCallback((submitFunc) => {
    submitFunctionRef.current = submitFunc;
  }, []);

  const handleOnPaymentSubmit = useCallback(
    ({ billingInfo: suppliedBillingInfo }) => {
      const promises = [updateSellerBillingInfo(mapBillingInfoDataToBillingAddress(suppliedBillingInfo))];
      const shouldUpdateCompanyOrPhone = getShouldUpdateCompanyOrPhone(suppliedBillingInfo);
      if (shouldUpdateCompanyOrPhone) {
        promises.push(updateCompanyNameAndPhone(suppliedBillingInfo?.company, suppliedBillingInfo?.phone));
      }
      Promise.all(promises).then(() => {
        dispatch(getSellerLight());
      });

      submitFunction();
    },
    // update the stored submit function when session changes to prevent stale submits
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [companyName, phone, submitFunction, dispatch, preloadedSession?.auctanePaySessionId],
  );

  useEffect(() => {
    // only set on session load
    setSelectedType(preloadedSession?.eligiblePaymentMethodTypes?.[0] || PaymentMethod.CreditCard);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreloadingSession]);

  return (
    <ManageModal
      actionMessage={messages.saveButton}
      cancelMessage={messages.cancelButton}
      headerMessage={messages.header}
      onAction={() => submitFunctionRef.current?.()}
      onCancel={handleOnCancel}
      onClose={handleOnCancel}
    >
      <div className={styles.wrapper}>
        {isPreloadingSession || isPolling ? (
          <LoadingIndicator className={styles.loader} />
        ) : (
          <AddPaymentMethodBody
            acceptedPaymentTypes={preloadedSession?.eligiblePaymentMethodTypes || [PaymentMethod.CreditCard]}
            creditCardConfig={creditCardConfig}
            currencyCode={sellerDefaultCurrencyCode}
            locale={locale}
            onError={handleError}
            onPaymentTypeChange={setSelectedType}
            onSubmit={handleOnPaymentSubmit}
            payByBankConfig={payByBankUsConfig}
            selectedPaymentType={selectedType}
            setSubmitFunction={handleManualSubmit}
          />
        )}
      </div>
    </ManageModal>
  );
};

export const AddPaymentMethodModal = (props) => (
  <TaxFormContextProvider source={PageEventSourceStrings.ADD_PAYMENT_METHOD}>
    <AddPaymentMethodModalContent {...props} />
  </TaxFormContextProvider>
);
